import React, { useState, useEffect } from 'react';
import { X, Heart, MapPin, Star, Shield } from 'lucide-react';
import axios from 'axios';

interface FavoriteUser {
  id: string;
  created_at: string;
  users: {
    id: string;
    name: string;
    city: string;
    user_stats: {
      average_rating: number;
      total_ratings: number;
      trust_score: number;
    } | null;
  };
}

interface FavoriteUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookId: string;
  bookTitle: string;
}

const FavoriteUsersModal: React.FC<FavoriteUsersModalProps> = ({
  isOpen,
  onClose,
  bookId,
  bookTitle
}) => {
  const [favoriteUsers, setFavoriteUsers] = useState<FavoriteUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && bookId) {
      fetchFavoriteUsers();
    }
  }, [isOpen, bookId]);

  const fetchFavoriteUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`/api/favorites/book/${bookId}/users`);
      setFavoriteUsers(response.data);
    } catch (error: any) {
      console.error('Error fetching favorite users:', error);
      setError(error.response?.data?.error || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTrustScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getTrustScoreLabel = (score: number) => {
    if (score >= 80) return 'Très fiable';
    if (score >= 60) return 'Fiable';
    return 'En cours d\'évaluation';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Heart className="h-6 w-6 text-red-500" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Utilisateurs intéressés
              </h2>
              <p className="text-sm text-gray-500 truncate max-w-md">
                {bookTitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-200 border-t-forest-600"></div>
              <span className="ml-3 text-gray-600">Chargement...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-2">⚠️</div>
              <p className="text-red-600">{error}</p>
            </div>
          ) : favoriteUsers.length === 0 ? (
            <div className="text-center py-8">
              <Heart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun utilisateur n'a encore mis ce livre en favori</p>
            </div>
          ) : (
            <div className="space-y-4">
              {favoriteUsers.map((favorite) => (
                <div
                  key={favorite.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-medium text-gray-900">
                          {favorite.users.name}
                        </h3>
                        {favorite.users.user_stats && (
                          <div className="flex items-center space-x-1">
                            <Star className="h-4 w-4 text-yellow-500" />
                            <span className="text-sm text-gray-600">
                              {favorite.users.user_stats.average_rating.toFixed(1)}
                              <span className="text-gray-400">
                                ({favorite.users.user_stats.total_ratings})
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-4 w-4" />
                          <span>{favorite.users.city}</span>
                        </div>
                        
                        {favorite.users.user_stats && (
                          <div className="flex items-center space-x-1">
                            <Shield className="h-4 w-4" />
                            <span className={getTrustScoreColor(favorite.users.user_stats.trust_score)}>
                              {favorite.users.user_stats.trust_score}/100
                            </span>
                            <span className="text-gray-400">
                              ({getTrustScoreLabel(favorite.users.user_stats.trust_score)})
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xs text-gray-400">
                        Ajouté le {formatDate(favorite.created_at)}
                      </p>
                      <button className="mt-2 px-3 py-1 bg-forest-600 text-white text-sm rounded-md hover:bg-forest-700 transition-colors">
                        Contacter
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {favoriteUsers.length} utilisateur{favoriteUsers.length > 1 ? 's' : ''} intéressé{favoriteUsers.length > 1 ? 's' : ''}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FavoriteUsersModal;
